import DefaultLayout from 'admin/components/DefaultLayout';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { Button, Dropdown, Input, Table, Select, SelectOption, Tabs } from 'ui';
import React from 'react';
import Filter from 'admin/components/Filter';
import { DownOutlined } from '@ant-design/icons';
import { PageDrawer } from 'ui/Drawer/Drawers/PageDrawer';
import useQueryApiClient from 'utils/useQueryApiClient';
import { message } from 'antd';
import { Pagination } from '../../../types/Pagination';
import { StyledButtonList } from '../../../styles/buttonList';

import {
  TagYes,
  Status,
  FilterRow,
  FilterItem,
  ActionsCell,
  CategoryCell,
} from './style';

type PageCategory = {
  id: number;
  title: string;
  published?: number;
};

interface PageFilters {
  search?: string;
  page_category?: number;
  status?: string;
  public?: string;
  page_size?: number;
}

const Pages = () => {
  const intl = useIntl();
  const [reload, setReload] = React.useState(0);
  const [categories, setCategories] = React.useState<PageCategory[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [selectedPageId, setSelectedPageId] = React.useState<
    number | undefined
  >();
  const [paginationStatus, setPaginationStatus] = React.useState<Pagination>(
    {}
  );
  const [filters, setFilters] = React.useState<PageFilters>({});

  useQueryApiClient({
    request: { url: '/api/v2/pages-category', method: 'GET' },
    onSuccess: (res: PageCategory[]) => setCategories(res || []),
  });

  const handleCreate = () => {
    setSelectedPageId(undefined);
    setIsDrawerOpen(true);
  };

  const handleEdit = (id: number) => {
    setSelectedPageId(id);
    setIsDrawerOpen(true);
  };

  const { appendData: deletePage } = useQueryApiClient({
    request: { url: `/api/v2/pages/:id`, method: 'DELETE' },
    onSuccess: () => {
      message.success(intl.formatMessage({ id: 'form.successfully_deleted' }));
      setReload((old) => old + 1);
    },
    onError: () => message.error(intl.formatMessage({ id: 'form.error' })),
  });

  const handleDelete = (id: number) => {
    if (window.confirm(intl.formatMessage({ id: 'general.delete_confirm' }))) {
      deletePage(null, { id });
    }
  };

  const statusOptions = React.useMemo(
    () => [
      {
        value: 'system_msg',
        label: intl.formatMessage({ id: 'pages.system' }),
      },
      { value: 'none', label: intl.formatMessage({ id: 'pages.none' }) },
    ],
    [intl]
  );

  const mapStatus = (val?: string) => {
    if (!val) return undefined;
    return val === 'system_msg' ? 1 : 0;
  };

  const handleFilterFinish = (values: Record<string, any>) => {
    const f: Record<string, any> = {};
    if (values.search) f.search = values.search;
    if (values.page_category) f.page_category = Number(values.page_category);
    const mapped = mapStatus(values.status);
    if (mapped === 0 || mapped === 1) f.system_msg = mapped;
    if (values.public === '1' || values.public === '0')
      f.public = values.public;
    if (values.page_size) f.page_size = Number(values.page_size);
    setFilters(f);
    setReload((r) => r + 1);
  };

  const columns = [
    {
      title: intl.formatMessage({ id: 'pages.id' }),
      dataIndex: 'id',
      width: 80,
      sorter: true,
      key: 'id',
    },
    {
      title: intl.formatMessage({ id: 'pages.title' }),
      dataIndex: 'title',
      ellipsis: true as const,
      sorter: true,
      key: 'title',
    },
    {
      title: intl.formatMessage({ id: 'general.category' }),
      dataIndex: 'category_title',
      key: 'category_title',
      sorter: true,
      render: (value: string) => (
        <CategoryCell>
          {value ? value : intl.formatMessage({ id: 'navigation.no_category' })}
        </CategoryCell>
      ),
    },
    {
      title: intl.formatMessage({ id: 'navigation.author' }),
      dataIndex: 'user_name',
      key: 'user_name',
      sorter: true,
    },
    {
      title: intl.formatMessage({ id: 'pages.status' }),
      dataIndex: 'news',
      key: 'status',
      render: (_: any, values: any) => (
        <Status>
          {values.system_msg ? (
            <div className="system">
              {intl.formatMessage({ id: 'pages.system' })}
            </div>
          ) : (
            <div />
          )}
        </Status>
      ),
    },
    {
      title: intl.formatMessage({ id: 'pages.public' }),
      dataIndex: 'public',
      key: 'public',
      sorter: true,
      render: (value: number) =>
        value ? (
          <TagYes>{intl.formatMessage({ id: 'general.yes' })}</TagYes>
        ) : (
          intl.formatMessage({ id: 'general.no' })
        ),
    },
    {
      title: intl.formatMessage({ id: 'general.created_at' }),
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      render: (value: string) => value && dayjs(value).format('DD.MM.YYYY'),
    },
    {
      title: intl.formatMessage({ id: 'general.updated_at' }),
      dataIndex: 'updated_at',
      key: 'updated_at',
      sorter: true,
      render: (value: string) => value && dayjs(value).format('DD.MM.YYYY'),
    },
    {
      title: intl.formatMessage({ id: 'general.actions' }),
      dataIndex: 'id',
      key: 'actions',
      fixed: 'right' as const,
      render: (value: number) => (
        <ActionsCell>
          <Dropdown
            className="actions"
            overlayClassName="status-actions"
            menu={{
              items: [
                {
                  label: intl.formatMessage({ id: 'general.edit' }),
                  key: 'edit',
                  onClick: () => handleEdit(value),
                },
                {
                  label: intl.formatMessage({ id: 'general.delete' }),
                  key: 'delete',
                  onClick: () => handleDelete(value),
                },
              ],
            }}
          >
            <Button
              label={intl.formatMessage({ id: 'general.actions' })}
              icon={<DownOutlined />}
              iconPosition="end"
              size="small"
            />
          </Dropdown>
        </ActionsCell>
      ),
    },
  ];

  const labelParsers = React.useMemo(
    () => ({
      page_category: (v: string | number) =>
        categories.find((c) => String(c.id) === String(v))?.title ?? v,
    }),
    [categories]
  );

  const items = [
    {
      key: 'all_products',
      label: intl.formatMessage({ id: 'navigation.products' }),
      children: (
        <>
          <Filter
            onFinish={handleFilterFinish}
            totalRecord={paginationStatus.total ?? 0}
            isLabelVisible
            customParse={labelParsers}
          >
            <FilterRow>
              <FilterItem>
                <Input
                  label={intl.formatMessage({ id: 'general.search' })}
                  name="search"
                  allowClear
                />
              </FilterItem>

              <FilterItem>
                <Select
                  name="page_category"
                  label={intl.formatMessage({ id: 'general.category' })}
                  showSearch={false}
                  allowClear
                >
                  {categories.map((c) => (
                    <SelectOption key={c.id} value={String(c.id)}>
                      {c.title}
                    </SelectOption>
                  ))}
                </Select>
              </FilterItem>

              <FilterItem>
                <Select
                  name="status"
                  label={intl.formatMessage({ id: 'pages.status' })}
                  showSearch={false}
                  allowClear
                >
                  {statusOptions.map((o) => (
                    <SelectOption key={o.value} value={o.value}>
                      {o.label}
                    </SelectOption>
                  ))}
                </Select>
              </FilterItem>

              <FilterItem>
                <Select
                  name="public"
                  label={intl.formatMessage({ id: 'pages.public' })}
                  showSearch={false}
                  allowClear
                >
                  <SelectOption value="1">
                    {intl.formatMessage({ id: 'general.yes' })}
                  </SelectOption>
                  <SelectOption value="0">
                    {intl.formatMessage({ id: 'general.no' })}
                  </SelectOption>
                </Select>
              </FilterItem>

              <FilterItem>
                <Button
                  type="primary"
                  label={intl.formatMessage({ id: 'general.select' })}
                  htmlType="submit"
                />
              </FilterItem>
            </FilterRow>
          </Filter>

          <Table
            url="/api/v2/pages"
            columns={columns}
            filter={filters}
            reload={reload}
            paginnationStatus={setPaginationStatus}
          />

          <PageDrawer
            key={isDrawerOpen ? (selectedPageId ?? 'create') : 'closed'}
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            pageId={selectedPageId}
            setReload={setReload}
          />
        </>
      ),
    },
  ];

  return (
    <DefaultLayout.PageLayout
      breadcrumbs={[
        { title: intl.formatMessage({ id: 'navigation.beginning' }) },
        { title: intl.formatMessage({ id: 'navigation.page' }) },
      ]}
    >
      <DefaultLayout.PageContent>
        <Tabs
          items={items}
          tabBarExtraContent={
            <StyledButtonList>
              <Button
                type="primary"
                label={intl.formatMessage({ id: 'general.add' })}
                onClick={handleCreate}
              />
            </StyledButtonList>
          }
        />
      </DefaultLayout.PageContent>
    </DefaultLayout.PageLayout>
  );
};

export default Pages;
